import { encounterField, posCoord } from '../../shared/array-types';

export interface TeleportOffer {
    id: string;
    abilityId: string;
    name: string;
    rangeFt: number;
    sizeFt?: number;
    expires: number;
    // Absent only on snapshots from the earlier transport, which already scoped/checked identity.
    caster_id?: string;
    encounter_id?: string;
    consumed?: boolean | number | string;
    origin?: { x: number; y: number; z: number } | null;
    context?: string[] | null;
    triggered?: boolean;
}
export type TeleportOfferIdentity = { caster_id: string; encounter_id: string };
export type TeleportOfferAgreement = { position: boolean; context: boolean };
const CONTEXT_KEYS = ['in_combat', 'active_combat_id', 'current_round', 'current_encounter_creature_id_turn', 'turn_phase'] as const;

/** A held lean row elides zero coordinates; an absent row is still unknown. */
export function teleportOfferPositionMatches(offer: TeleportOffer, caster: unknown): boolean | null {
    if (!caster || !offer.origin) return null;
    return (['x', 'y', 'z'] as const).every(axis =>
        Math.abs((posCoord(caster, `${axis}_pos`) ?? 0) - Number(offer.origin![axis])) <= 0.011);
}

export function teleportOfferContextMatches(offer: TeleportOffer, encounter: unknown): boolean | null {
    if (!encounter || !Array.isArray(offer.context) || offer.context.length !== CONTEXT_KEYS.length) return null;
    const compared = offer.triggered === true ? 2 : CONTEXT_KEYS.length;
    for (let i = 0; i < compared; i++) {
        const key = CONTEXT_KEYS[i]!;
        const value = encounterField(encounter, key);
        const normalized = (part: unknown): string => key === 'in_combat' || key === 'current_round'
            ? String(Number(part ?? 0)) : String(part ?? '');
        if (normalized(value) !== normalized(offer.context[i])) return false;
    }
    return true;
}

/** Expiry/consumption/identity are facts. A local position/turn mismatch becomes proof only after
 * this browser has agreed with that offer, so an older cache cannot expire a newly earned choice. */
export function teleportOfferIsCurrent(offer: TeleportOffer, agreed: TeleportOfferAgreement,
    identity: TeleportOfferIdentity, caster: unknown, encounter: unknown, nowS: number): boolean {
    if (!offer.id || (offer.consumed && offer.consumed !== '0') || !(Number(offer.expires) > nowS)) return false;
    if (offer.caster_id !== undefined && offer.caster_id.toUpperCase() !== identity.caster_id.toUpperCase()) return false;
    if (offer.encounter_id !== undefined && offer.encounter_id.toUpperCase() !== identity.encounter_id.toUpperCase()) return false;
    if (!offer.origin || !(['x', 'y', 'z'] as const).every(axis => Number.isFinite(offer.origin![axis]))
        || !Array.isArray(offer.context) || offer.context.length !== CONTEXT_KEYS.length) return false;
    const position = teleportOfferPositionMatches(offer, caster);
    if (position === true) agreed.position = true;
    else if (position === false && agreed.position) return false;
    const turn = teleportOfferContextMatches(offer, encounter);
    if (turn === true) agreed.context = true;
    else if (turn === false && agreed.context) return false;
    return true;
}

/** Pause is an action gate, not offer consumption or expiry; a GM can still resolve it. */
export function teleportOfferPlayAllowed(mode: unknown, requesterIsGm: boolean): boolean {
    return mode !== 'paused' || requesterIsGm;
}
